import type React from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../theme";
import { CourseTabProps } from "../types/course.types";
import CustomTextField from "../../../../components/ui/Textfield";
import DynamicListField from "../fields/DynamicListFields";
import { SectionTitle } from "./AboutCourse";

const SummaryTab: React.FC<CourseTabProps> = ({ formData, onFormChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Duration Fields */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomTextField
            fullWidth
            type="number"
            label="Duration (Hours)"
            value={formData.durationHours}
            onChange={(e) =>
              onFormChange({
                durationHours: Number.parseFloat(e.target.value) || 0,
              })
            }
            variant="outlined"
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomTextField
            fullWidth
            type="number"
            label="Duration (Weeks)"
            value={formData.durationWeeks}
            onChange={(e) =>
              onFormChange({
                durationWeeks: Number.parseFloat(e.target.value) || 0,
              })
            }
            variant="outlined"
            inputProps={{ min: 0 }}
          />
        </Grid>
      </Grid>

      {/* Difficulty and Course Type */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Difficulty Level</InputLabel>
            <Select
              value={formData.difficultyLevel}
              onChange={(e) =>
                onFormChange({
                  difficultyLevel: e.target
                    .value as CourseTabProps["formData"]["difficultyLevel"],
                })
              }
              label="Difficulty Level"
            >
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Course Type</InputLabel>
            <Select
              value={formData.courseType}
              onChange={(e) =>
                onFormChange({
                  courseType: e.target
                    .value as CourseTabProps["formData"]["courseType"],
                })
              }
              label="Course Type"
            >
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {" "}
          <SectionTitle>Key Outcomes</SectionTitle>
          <DynamicListField
            items={formData.learningObjectives}
            onItemsChange={(items) =>
              onFormChange({ learningObjectives: items })
            }
            placeholder="Add a key outcomes"
          />
        </Grid>
      </Grid>

      {/* Course Summary Card */}
      <Paper
        sx={{
          p: 3,
          backgroundColor: colors.primary[800],
          borderRadius: 2,
          boxShadow: 0,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Course Summary
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Duration
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formData.durationHours}h / {formData.durationWeeks}w
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Difficulty
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, textTransform: "capitalize" }}
              >
                {formData.difficultyLevel}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Key Outcomes
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formData.learningObjectives.length}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Type
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, textTransform: "capitalize" }}
              >
                {formData.courseType}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Skills
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formData.skills.length}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Requirements
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formData.requirements.length}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SummaryTab;
